import { applyFilters, callbackStats, can, financeByCurrency, reconcile } from '@urbanrats/ops-core';
import { Kpi, PageHead, Section } from '../components/common.tsx';
import { fmtAtomic, hrefWith, useCtx } from '../lib.ts';
import { TeamPanel } from '../components/Team.tsx';

export function Overview() {
  const { data, filters, user, backend } = useCtx();
  const f = applyFilters(data, filters);
  const fin = financeByCurrency(f.bets);
  const cb = callbackStats(data.callbacks.filter((c) => f.txs.some((x) => x.key === c.txKey) || (c.outcome === 'PENDING' && f.bets.some((b) => `pending:${b.key}` === c.txKey))));
  const byState = new Map<string, number>();
  for (const r of f.rounds) byState.set(r.state, (byState.get(r.state) ?? 0) + 1);
  const recon = reconcile({ players: f.players, bets: data.bets.filter((b) => f.players.some((p) => p.playerRef === b.playerRef)), txs: data.txs.filter((x) => f.players.some((p) => p.playerRef === x.playerRef)) });
  const activePlayers = new Set(f.bets.map((b) => b.playerRef)).size;
  const h = (path: string, extra: Record<string, string | null> = {}) => hrefWith(path, filters, extra);
  return (
    <>
      <PageHead title="Overview">
        Every figure links to the records it is computed from. Amounts are per currency: nothing is added across currencies (no conversion policy exists). GGR = settled cash stake − settled cash payout; refunds excluded; promo cost shown separately.
      </PageHead>
      {backend.kind === 'api' && backend.server && user.role !== 'PARTNER_ADMIN' && (
        <Section title="Team" testId="overview-team">
          <TeamPanel compact />
        </Section>
      )}
      {fin.length === 0 && (
        <p className="empty" role="status" data-testid="overview-empty">
          No bets match these filters.
        </p>
      )}
      {can(user, 'view.bets') &&
        fin.map((r) => {
          const m = (v: bigint) => fmtAtomic(v, r.scale, r.currency);
          return (
            <Section key={r.currency} title={`${r.currency} · ${r.betCount} accepted bets`} testId={`kpis-${r.currency}`}>
              <div className="kpis">
                <Kpi label="Settled cash stake" value={m(r.settledCashStake)} to={h('/bets', { currency: r.currency, state: 'SETTLED', funding: 'CASH' })} basis="settled cash bets" />
                <Kpi label="Settled cash payout" value={m(r.settledCashPayout)} to={h('/bets', { currency: r.currency, state: 'SETTLED', funding: 'CASH' })} basis="credits of settled cash bets" />
                <Kpi label="GGR (cash)" value={m(r.ggr)} negative={r.ggr < 0n} to={h('/finance', { currency: r.currency })} basis="finance breakdown" testId={`ggr-${r.currency}`} />
                <Kpi label="Promo cost" value={m(r.promoCost)} to={h('/bets', { currency: r.currency, state: 'SETTLED', funding: 'FREEBET' })} basis="freebet credits + Lightning/boost uplift" />
                <Kpi label="Refunds" value={m(r.refunds)} to={h('/bets', { currency: r.currency, state: 'VOIDED' })} basis="voided bets" />
                <Kpi label="Pending credit" value={m(r.pendingCredit)} to={h('/bets', { currency: r.currency, state: 'CREDIT_PENDING' })} basis="bets waiting for the wallet" />
                <Kpi label="Open cash stake" value={m(r.openCashStake)} to={h('/risk', { currency: r.currency })} basis="unresolved rounds (risk)" />
              </div>
            </Section>
          );
        })}
      <Section title="Operations">
        <div className="kpis">
          {can(user, 'view.rounds') &&
            ['BETTING_OPEN', 'BETTING_CLOSED', 'CAPTURING', 'REVIEW_REQUIRED', 'SETTLED', 'VOIDED'].map((s) => (
              <Kpi key={s} label={`Rounds ${s}`} value={byState.get(s) ?? 0} to={h('/rounds', { state: s })} basis="round list" testId={`rounds-${s}`} />
            ))}
          {can(user, 'view.players') && <Kpi label="Players with bets" value={activePlayers} to={h('/players')} basis="pseudonymous players" />}
          <Kpi label="Wallet callbacks" value={`${cb.ok}/${cb.attempts} OK · ${cb.timeouts} timeouts · ${cb.pending} pending`} to={h('/transactions')} basis={cb.simulated ? 'transport SIMULATED (demo)' : 'callback attempts'} />
          <Kpi label="Callback p95 latency" value={cb.p95LatencyMs === null ? '—' : `${cb.p95LatencyMs} ms`} to={h('/transactions')} basis={cb.simulated ? 'simulated latencies' : 'measured'} />
          {can(user, 'view.reconciliation') && (
            <Kpi label="Reconciliation" value={recon.issues.length === 0 ? `OK · ${recon.pending.length} pending` : `${recon.issues.length} issue(s)`} negative={recon.issues.length > 0} to={h('/reconciliation')} basis="ledger vs bets" />
          )}
        </div>
      </Section>
    </>
  );
}
